// New to this? (DIRECTION A2, PLAN-2026-10-18-families-and-newcomers §8,
// B-444): four short lines on the pre-draft screen, under *What happens
// next*, for somebody who has never played — the draft, a lineup, points,
// winning — in this league's own terms, until they put it away.
//
// HELP IS THE CLIENT'S OWN BUSINESS, so the words live here; the winning line
// is the format's own (`formatWords`), which the create form shows too, so a
// total-points league is not told about opponents. It names no player and
// tells nobody what to do with one (DIRECTION U3).
//
// PUT AWAY FOR THIS PERSON ON THIS DEVICE: *I've played before* is kept in
// the browser. It is not a setting worth a server's round trip, and a second
// device asking once more costs nothing.

import { useState } from 'react';
import { formatWords, type LeagueRef, type LeagueSettings } from '@fsp/domain';
import { storageKey } from '../../data/storage';
import { Panel } from '../../kit/Panel';
import { Button, Stack, Text } from '../../kit/ui';

const PUT_AWAY_KEY = storageKey('new-to-this.put-away.v1');

function readPutAway(): boolean {
  try {
    return localStorage.getItem(PUT_AWAY_KEY) === '1';
  } catch {
    return false;
  }
}

function writePutAway(): void {
  try {
    localStorage.setItem(PUT_AWAY_KEY, '1');
  } catch {
    // Storage blocked: it stays put away for this visit only.
  }
}

/** The format's own words on who wins, or null for a format these words do not know. */
function winningLine(format: LeagueRef['format']): string | null {
  // A format id from a newer server than this client is looked up and found
  // missing, rather than trusted: the line is left out.
  const words = formatWords(format.id as LeagueSettings['format']['id']) as
    ReturnType<typeof formatWords> | undefined;
  return words?.explainer.find((e) => e.label === 'Who wins')?.text ?? null;
}

export function NewToThis({
  league,
  auction,
  carried = false,
}: {
  league: LeagueRef;
  /** An auction draft bids where a pick draft takes turns. */
  auction: boolean;
  /** A dynasty's season after its first: every roster carried, the draft adding to them (B-535). */
  carried?: boolean;
}) {
  const [putAway, setPutAway] = useState(readPutAway);
  if (putAway) return null;
  const winning = winningLine(league.format);
  const lines: Array<{ label: string; text: string }> = [
    {
      label: 'The draft',
      text: carried
        ? 'Every team keeps its roster from last season, and the draft adds a few new players to each.'
        : auction
          ? 'Everyone bids on real players from the same budget until every team is full. They are your team for the season.'
          : 'Everyone picks real players in turn until every team is full. They are your team for the season.',
    },
    {
      label: 'Your lineup',
      text: league.format.bestBall
        ? 'There is none to set: after each week’s games, your highest scorers are the ones that count.'
        : 'Before each week’s games, choose who starts. Only starters score, and the league’s rules say when a lineup locks.',
    },
    {
      label: 'Points',
      text: 'Your starters score from what they do in their real games. The league’s scoring says how much each thing is worth.',
    },
    ...(winning === null ? [] : [{ label: 'Winning', text: winning }]),
  ];
  return (
    <Panel title="New to this?">
      <Stack gap={2}>
        {lines.map((l) => (
          <Text key={l.label} block>
            <strong>{l.label}.</strong> {l.text}
          </Text>
        ))}
        <div>
          <Button
            size="xs"
            variant="quiet"
            onClick={() => {
              writePutAway();
              setPutAway(true);
            }}
          >
            I’ve played before
          </Button>
        </div>
      </Stack>
    </Panel>
  );
}
